import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Gem, Home, Shirt, Plus, CalendarDays, History as HistoryIcon, Settings as SettingsIcon, Plane, Store, LogOut, UserRound, Tag, BookHeart, HelpCircle, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, money, avatarUrl } from "@/lib/api";
import { useLiveStats } from "@/lib/worth";
import { CountUp } from "@/components/CountUp";
import { applyTheme } from "@/lib/theme";
import { Brand } from "@/components/Brand";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationsBell } from "@/components/NotificationsBell";

export const NAV = [
  { to: "/home", label: "Home", icon: Home, testid: "dashboard" },
  { to: "/inventory", label: "Closet", icon: Shirt, testid: "inventory" },
  { to: "/worth", label: "Threadworth", icon: Gem, testid: "worth" },
  { to: "/looks", label: "Lookbook", icon: BookHeart, testid: "looks" },
  { to: "/planner", label: "Planner", icon: CalendarDays, testid: "planner" },
  { to: "/trips", label: "Trips", icon: Plane, testid: "trips" },
  { to: "/market", label: "Market", icon: Store, testid: "market" },
  { to: "/social", label: "Social", icon: Users, testid: "social" },
];
const MOBILE_TABS = ["dashboard", "inventory", "add", "market", "social"];
const ADD = { to: "/add", label: "Add", icon: Plus, testid: "add" };

const UserMenu = ({ user, logout, navigate }) => {
  const initials = (user?.display_name || user?.handle || "?").slice(0, 2).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center rounded-full p-0.5 ring-1 ring-transparent hover:ring-border transition-shadow" data-testid="user-menu-trigger" aria-label="Account menu">
          <Avatar className="h-8 w-8 text-[11px]">
            {user.avatar_public && <AvatarImage src={avatarUrl(user.handle)} alt={user.handle} />}
            <AvatarFallback className="bg-foreground text-background font-semibold">{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-2xl p-1.5 shadow-lift">
        <div className="px-2.5 py-2"><p className="text-sm font-semibold truncate">{user.display_name || user.handle}</p><p className="text-xs text-muted-foreground truncate">@{user.handle}</p></div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="rounded-lg"><Link to={`/u/${user.handle}`} data-testid="user-menu-profile"><UserRound className="h-4 w-4 mr-2" /> My profile</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg"><Link to={`/u/${user.handle}?tab=manage`} data-testid="user-menu-listings"><Tag className="h-4 w-4 mr-2" /> Sell & trade</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg md:hidden"><Link to="/planner"><CalendarDays className="h-4 w-4 mr-2" /> Planner</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg md:hidden"><Link to="/trips"><Plane className="h-4 w-4 mr-2" /> Trips</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg"><Link to="/history" data-testid="user-menu-history"><HistoryIcon className="h-4 w-4 mr-2" /> Wear history</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg"><Link to="/settings" data-testid="user-menu-settings"><SettingsIcon className="h-4 w-4 mr-2" /> Settings</Link></DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg"><Link to="/welcome" data-testid="user-menu-tour"><HelpCircle className="h-4 w-4 mr-2" /> How ThreadList works</Link></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="rounded-lg" onClick={async () => { await logout(); }} data-testid="user-menu-logout"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Live Threadworth readout in the nav. Gold = your closet's value. Animates whenever pieces are added, sold or re-valued. */
const WorthChip = () => {
  const stats = useLiveStats(20000);
  if (!stats) return null;
  return (
    <Link to="/worth" className="hidden md:inline-flex items-center gap-2 rounded-full border border-border px-3 h-9 text-sm hover:bg-secondary transition-colors" data-testid="nav-threadworth" title={`Threadworth · ${stats.total_items} pieces`}>
      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
      <span className="text-muted-foreground text-xs hidden lg:inline">Threadworth</span>
      <CountUp value={stats.total_current_worth || 0} className="font-mono-num font-semibold" testid="nav-threadworth-value" />
    </Link>
  );
};

export default function Layout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { applyTheme(); }, []);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      {user?.is_demo && (
        <div className="bg-foreground text-background text-center text-xs py-1.5 px-4" data-testid="demo-banner">
          Demo closet — add, style and sell freely. Nothing here touches a real account.
          <Link to="/login?mode=register" className="underline underline-offset-4 ml-2" onClick={() => { /* leave the demo */ }}>Create your own</Link>
        </div>
      )}
      {/* Top navigation (all breakpoints) */}
      <header className={`sticky top-0 z-40 glass transition-shadow ${scrolled ? "border-b border-border" : "border-b border-transparent"}`} data-testid="top-nav">
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
          <Brand to="/home" />
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            {NAV.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`sidenav-${l.testid}-link`}
                className={({ isActive }) => `relative px-3.5 h-9 inline-flex items-center rounded-full text-[14px] font-medium transition-colors ${isActive ? "text-foreground bg-secondary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/70"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <WorthChip />
            <Link to="/add" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full whitespace-nowrap bg-foreground text-background text-sm font-semibold press transition-transform hover:bg-neutral-800" data-testid="sidenav-add-link"><Plus className="h-4 w-4" /> Add pieces</Link>
            <NotificationsBell />
            {user && <UserMenu user={user} logout={logout} navigate={navigate} />}
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-[1240px] px-5 sm:px-8 py-6 md:py-10 pb-28 md:pb-16" data-testid="main-content">
        <Outlet />
      </main>

      {/* Mobile bottom tabs */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-border pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
        <div className="grid grid-cols-5 h-16">
          {MOBILE_TABS.map((t) => (t === "add" ? ADD : NAV.find((n) => n.testid === t))).map((l) => {
            const Icon = l.icon;
            const isAdd = l.testid === "add";
            return (
              <NavLink key={l.to} to={l.to} data-testid={`bottomnav-${l.testid}-link`}
                className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 text-[10px] font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                {({ isActive }) => isAdd ? (
                  <span className="-mt-6 inline-flex h-14 w-14 items-center justify-center rounded-full bg-foreground text-background shadow-lift press transition-transform"><Plus className="h-6 w-6" /></span>
                ) : (<>
                  <Icon className={`h-5 w-5 ${isActive ? "" : ""}`} strokeWidth={isActive ? 2.4 : 1.8} />
                  <span>{l.label}</span>
                </>)}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
